import { ImageURISource } from 'react-native'
import deepmerge from 'deepmerge'
import { logger } from 'APP/Lib/Datadog'
import { getColourScheme, ColourScheme } from './AppearanceUtils'
import {
  brandVersionedI18nFiles,
  brandVersionedThemeJsons,
  brandVersionedThemeFiles,
} from './brandVersionedFiles'

export function getBrandVersionedI18n(
  language: string,
  baseI18n: Record<string, unknown>,
  brandVersion?: string
) {
  let versionedI18n: Record<string, unknown> | undefined

  if (brandVersion) {
    try {
      versionedI18n = brandVersionedI18nFiles(`./${language}_${brandVersion}.json`)
    } catch (_) {
      // Do nothing
    }

    if (!versionedI18n) {
      versionedI18n = {}

      if (__DEV__) {
        logger.warn(
          `Brand versioned i18n file for ${language} not found. Using default translations.`
        )
      }
    }
  }

  return mergeWithBase(baseI18n, versionedI18n)
}

function getColorValues(
  data: Record<string, string> | undefined,
  ColorLibrary: Record<string, string>
) {
  return Object.entries(data ?? {}).reduce((acc, [key, value]) => {
    acc[key] = ColorLibrary[value]
    return acc
  }, {} as Record<string, string>)
}

export function getBrandVersionedColors(
  baseColors = {},
  darkColors = {},
  ColorLibrary: Record<string, string>,
  brandVersion?: string
) {
  const colorScheme = getColourScheme()
  const colors: Record<string, string> =
    colorScheme === ColourScheme.DARK ? deepmerge(baseColors, darkColors) : baseColors
  let brandVersionColorsBase = {}

  if (!brandVersion) {
    return colors
  } else {
    try {
      const versionedColorFile = brandVersionedThemeJsons(`./ColorsBase_${brandVersion}.json`)
      brandVersionColorsBase = getColorValues(versionedColorFile, ColorLibrary) || {}

      if (colorScheme === ColourScheme.DARK) {
        let brandVersionColorsDark = {}
        const darkVersionedColorFile = brandVersionedThemeJsons(`./ColorsDark_${brandVersion}.json`)
        brandVersionColorsDark = getColorValues(darkVersionedColorFile, ColorLibrary) || {}
        brandVersionColorsBase = deepmerge(brandVersionColorsBase, brandVersionColorsDark)
      }
    } catch (_) {
      // Do nothing
    }
    if (Object.keys(brandVersionColorsBase).length === 0) {
      if (__DEV__) {
        logger.warn(
          `Brand versioned colors file for ${brandVersion} not found. Using default colors.`
        )
      }
    }
  }

  return deepmerge(colors, brandVersionColorsBase)
}

export function getBrandVersionedImages(baseImages = {}, darkImages = {}, brandVersion?: string) {
  const images: Record<string, ImageURISource> =
    getColourScheme() === ColourScheme.DARK ? deepmerge(baseImages, darkImages) : baseImages
  let brandVersionImages = {}

  if (brandVersion) {
    try {
      const versionedImageFile = brandVersionedThemeFiles(`./${brandVersion}.js`)

      brandVersionImages = versionedImageFile?.Images || {}
    } catch (_) {
      // Versioned images not found, defaulting to base version images
      if (__DEV__) {
        logger.warn(
          `Versioned images not found, defaulting to base version images'${brandVersion}'`
        )
      }
    }
  }

  return deepmerge(images, brandVersionImages)
}

export function mergeWithBase(
  base: Record<string, unknown> = {},
  patch: Record<string, unknown> = {}
): Record<string, unknown> {
  return deepmerge(base, patch, {
    arrayMerge: (_destinationArray, sourceArray) => sourceArray,
  })
}
